import { getSessionToken } from "./session";

export interface UserMusicTrack {
  readonly id: string;
  readonly filename: string;
  readonly contentType: string;
  readonly sizeBytes: number;
  readonly createdAt: number;
}

function endpoint(): string {
  const value = import.meta.env.VITE_GAME_API_URL as string | undefined;
  if (!value) throw new Error("联网音乐库尚未配置");
  return value.replace(/\/$/u, "");
}

function authHeaders(): HeadersInit {
  const token = getSessionToken();
  if (!token) throw new Error("请先登录后使用音乐库");
  return { authorization: `Bearer ${token}` };
}

async function expect(response: Response): Promise<Response> {
  if (response.ok) return response;
  const value = await response.json().catch(() => null) as { error?: { message?: string } } | null;
  throw new Error(value?.error?.message ?? "音乐库暂时不可用");
}

export const userMusicApi = {
  async list(): Promise<readonly UserMusicTrack[]> {
    const response = await expect(await fetch(`${endpoint()}/music/tracks`, { headers: authHeaders() }));
    return ((await response.json()) as { tracks: readonly UserMusicTrack[] }).tracks;
  },
  async upload(file: File): Promise<UserMusicTrack> {
    const form = new FormData();
    form.set("file", file);
    const response = await expect(await fetch(`${endpoint()}/music/tracks`, { method: "POST", headers: authHeaders(), body: form }));
    return ((await response.json()) as { track: UserMusicTrack }).track;
  },
  async remove(id: string): Promise<void> {
    await expect(await fetch(`${endpoint()}/music/tracks/${encodeURIComponent(id)}`, { method: "DELETE", headers: authHeaders() }));
  },
  async blob(id: string, signal?: AbortSignal): Promise<Blob> {
    const response = await expect(await fetch(`${endpoint()}/music/tracks/${encodeURIComponent(id)}/content`, { headers: authHeaders(), ...(signal ? { signal } : {}) }));
    return response.blob();
  },
};

export class UserMusicPlayer {
  private readonly audio = new Audio();
  private objectUrl: string | null = null;
  private trackId: string | null = null;

  constructor() {
    this.audio.loop = true;
    this.audio.preload = "auto";
  }

  async prepare(trackId: string, signal?: AbortSignal): Promise<void> {
    if (this.trackId === trackId && this.objectUrl) return;
    const blob = await userMusicApi.blob(trackId, signal);
    this.releaseUrl();
    this.objectUrl = URL.createObjectURL(blob);
    this.trackId = trackId;
    this.audio.src = this.objectUrl;
    this.audio.load();
  }

  async play(volume: number): Promise<void> {
    this.audio.volume = Math.min(1, Math.max(0, volume));
    if (this.audio.src) await this.audio.play();
  }

  setVolume(volume: number): void {
    this.audio.volume = Math.min(1, Math.max(0, volume));
  }

  pause(): void {
    this.audio.pause();
  }

  dispose(): void {
    this.audio.pause();
    this.audio.removeAttribute("src");
    this.audio.load();
    this.releaseUrl();
    this.trackId = null;
  }

  private releaseUrl(): void {
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = null;
  }
}
